import { useActiveContentContext } from '@src/contexts/ActiveContentContext'
import { useUniversalEffect } from '@src/hooks/useUniversalEffect'
import { parseAsString, useQueryState } from 'nuqs'
import { createContext, FC, ReactNode, useContext, useMemo, useState } from 'react'

export type MatchingContextValue = {
  activeUserMatchId?: string
  breadcrumbs?: {
    title: string
    href: string
  }[]
  chatThreadId?: string
  resultsView?: 'profiles' | 'grid' | 'list' | string
  setBreadcrumbs?: (breadcrumbs: MatchingContextValue['breadcrumbs']) => void
  setResultsView?: (resultsView: MatchingContextValue['resultsView']) => void
  setActiveUserMatchId?: (resultsView: MatchingContextValue['activeUserMatchId']) => void
  setChatThreadId?: (threadId: string) => void
}

export const MatchingContext = createContext<MatchingContextValue>({})

export const useMatchingContext = () => useContext(MatchingContext)

export const MatchingContextProvider: FC<MatchingContextValue & { children?: ReactNode }> = props => {
  const activeContent = useActiveContentContext()
  const [resultsView, setResultsView] = useQueryState<MatchingContextValue['resultsView']>(
    'view',
    parseAsString.withDefault('profiles')
  )
  const [activeUserMatchId, setActiveUserMatchId] = useState(null)
  const [chatThreadId, setChatThreadId] = useQueryState('threadId', parseAsString)

  useUniversalEffect(() => {
    activeContent.setSiteSection('matching')
  })

  const [breadcrumbs, setBreadcrumbs] = useState<MatchingContextValue['breadcrumbs']>([
    { title: 'Members', href: '/members' },
    { title: 'Matching', href: '/members/matching' },
  ])

  const contextValue = useMemo(
    () => ({
      activeUserMatchId,
      breadcrumbs,
      chatThreadId,
      setBreadcrumbs,
      resultsView,
      setResultsView,
      setActiveUserMatchId,
      setChatThreadId,
    }),
    [activeUserMatchId, breadcrumbs, resultsView, chatThreadId]
  )

  return <MatchingContext.Provider value={contextValue}>{props.children}</MatchingContext.Provider>
}
